import React from 'react'
import styled, { keyframes } from 'styled-components'
import { ReactComponent as CloseIcon } from 'assets/images/close.svg'

export default function NewsDetailModal({ item, onClose }) {
  return (
    <Overlay>
      <Modal>
        <Header>
          <Title>
            {item.title}
          </Title>
          <CloseBtn onClick={onClose}>
            <CloseIcon />
          </CloseBtn>
        </Header>

        <AnimatedContent>
          <MediaColumn>
            {item.iframe ? (
              <div
                className="iframe-container"
                dangerouslySetInnerHTML={{ __html: item.iframe }}
              />
            ) : item.video ? (
              <video controls muted playsInline>
                <source src={item.video} type="video/mp4" />
              </video>
            ) : item.image ? (
              <img src={item.image} alt={item.title} />
            ) : (
              <EmptyState>No media available</EmptyState>
            )}
          </MediaColumn>

          <TextColumn>
            {item.description && (
              <Description
                dangerouslySetInnerHTML={{ __html: item.description }}
              />
            )}

            {item.link && (
              <LinkButton
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more →
              </LinkButton>
            )}
          </TextColumn>
        </AnimatedContent>
      </Modal>
    </Overlay>
  )
}


const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.97); }
  to { opacity: 1; transform: scale(1); }
`

const slideLeft = keyframes`
  from { opacity: 0; transform: translateX(-30px); }
  to { opacity: 1; transform: translateX(0); }
`

const slideRight = keyframes`
  from { opacity: 0; transform: translateX(30px); }
  to { opacity: 1; transform: translateX(0); }
`

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 18, 24, 0.6);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 24px;
  animation: ${fadeIn} 0.25s ease;
`

const Modal = styled.div`
  width: 900px;
  max-width: 90vw;
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  animation: ${fadeIn} 0.3s ease;
  display: flex;
  flex-direction: column;
  max-height: 90vh;

  @media (max-width: 768px) {
    width: 95vw;
    max-width: 95vw;
    border-radius: 12px;
    max-height: 85vh;
  }
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 32px;
  border-bottom: 1px solid #f0f0f0;
  background: #fafafa;
`

const Title = styled.h3`
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  color: #111;
`

const CloseBtn = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.15s ease;

  svg {
    width: 22px;
    height: 22px;
    stroke: #d14724;
  }

  &:hover {
    transform: rotate(90deg);
    svg {
      opacity: 0.8;
    }
  }
`

const AnimatedContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 36px;
  padding: 36px 40px;
  align-items: center;
  overflow-y: auto;

  @media (max-width: 768px) {
    padding: 24px 20px;
    gap: 20px;
  }
`

const MediaColumn = styled.div`
  width: 100%;
  border-radius: 14px;
  overflow: hidden;
  background: #f9f9f9;
  display: flex;
  justify-content: center;
  align-items: center;
  animation: ${slideLeft} 0.45s ease forwards;

  iframe, video {
    width: 100%;
    height: auto;
    aspect-ratio: 16/9;
    min-height: auto;
    border: none;
    border-radius: 14px;
    object-fit: cover;
  }

  img {
    width: 100%;
    max-height: 420px;
    object-fit: contain;
    border-radius: 14px;
  }

  .iframe-container {
    width: 100%;
    aspect-ratio: 16/9;
    border-radius: 14px;
    overflow: hidden;
  }

  @media (max-width: 768px) {
    animation: ${fadeIn} 0.3s ease;
  }
`

const TextColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 20px;
  color: #333;
  animation: ${slideRight} 0.5s ease forwards;
  width: 100%;

  @media (max-width: 768px) {
    animation: ${fadeIn} 0.4s ease;
  }
`

const Description = styled.div`
  font-size: 15.5px;
  line-height: 1.6;
  color: #444;

  p { margin: 0 0 14px; }
  strong { color: #000; }
`

const LinkButton = styled.a`
  align-self: flex-start;
  background: #d14724;
  color: #fff;
  padding: 11px 22px;
  border-radius: 8px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.25s ease;

  &:hover {
    background: #b93f20;
    transform: translateY(-2px);
    box-shadow: 0 4px 10px rgba(209, 71, 36, 0.25);
  }

  @media (max-width: 480px) {
    width: 100%;
    text-align: center;
    box-sizing: border-box;
  }
`

const EmptyState = styled.div`
  width: 100%;
  height: 320px;
  background: #f4f4f4;
  color: #999;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
`
